import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H1, H2, H3 } from '@liveauctioneers/hammer-ui-core/text';

import PageTitle from '@/components/PageTitle/PageTitle';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import PrivacyPageHeaderMeta from './PrivacyPageHeaderMeta';

const PrivacyNotice = () => {
    const formatMessage = useAppFormatMessage();

    return (
        <div className="mx-auto w-full max-w-[1240px] px-sm pb-lg">
            <PrivacyPageHeaderMeta />
            <PageTitle title={formatMessage('privacyPolicy', 'title')} />
            <H1 className="mb-md text-center">Privacy Notice</H1>
            <BodyPrimary className="mb-sm">
                This privacy notice explains how Auction Technology Group ("ATG") companies use personal data across our
                companies and the following websites:
            </BodyPrimary>
            <ul className="mb-sm ml-md list-disc">
                <li className="mb-2xs">
                    AntiquesTradeGazette.com operated by Metropress Ltd incorporated in England and Wales
                </li>
                <li className="mb-2xs">
                    AuctionMobility.com operated by Auction Mobility LLC incorporated in Delaware USA
                </li>
                <li className="mb-2xs">
                    AuctionTechnologyGroup.com operated by Metropress Ltd incorporated in England and Wales
                </li>
                <li className="mb-2xs">
                    BidSpotter.co.uk in the UK operated by Metropress Ltd incorporated in England and Wales
                </li>
                <li className="mb-2xs">BidSpotter.com operated by Proxibid Inc incorporated in Delaware USA</li>
                <li className="mb-2xs">
                    i-Bidder.com in the UK operated by Metropress Ltd incorporated in England and Wales
                </li>
                <li className="mb-2xs">Jasper52 operated by Live Auctioneers LLC incorporated in New York USA</li>
                <li className="mb-2xs">
                    Liveauctioneers.com operated by Live Auctioneers LLC incorporated in New York USA
                </li>
                <li className="mb-2xs">Proxibid.com operated by Proxibid Inc. incorporated in Delaware USA</li>
                <li className="mb-2xs">
                    the-saleroom.com in the UK operated by Metropress Ltd incorporated in England and Wales
                </li>
                <li className="mb-2xs">Wavebid.com operated by Proxibid Inc incorporated in Delaware USA</li>
            </ul>
            <BodyPrimary className="mb-sm">
                By creating an account on any of the websites listed above you agree to be bound by the terms and
                conditions of this Privacy Policy. If you do not agree to its terms and conditions you should not use or
                access our websites. By accepting the Privacy Policy you expressly consent to our use and disclosure of
                your personal data in accordance with the Policy.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Auction Technology Group Germany GmbH operates as our European representative under EU GDPR.
            </BodyPrimary>
            <H2 className="mb-xs mt-md">How to contact us</H2>
            <BodyPrimary className="mb-sm">
                We are here to help you with any questions or concerns you may have about our use of your personal data.
                If you require further information, wish to make a request or exercise your rights under applicable laws
                you can contact us as follows:
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                <strong>Via the website</strong>
            </BodyPrimary>
            <BodyPrimary className="mb-sm">Via the contact us forms on the websites.</BodyPrimary>
            <BodyPrimary className="mb-sm">Via the messaging service on your account.</BodyPrimary>
            <BodyPrimary className="mb-sm">
                <strong>Via phone</strong>
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                US:
                <ul className="mb-sm ml-md list-disc">
                    <li className="mb-2xs">BidSpotter.com: +1 866-597-2437</li>
                    <li className="mb-2xs">LiveAuctioneers: +1 888-600-2437 ext 1</li>
                    <li className="mb-2xs">Proxibid: +1 877-505-7770</li>
                    <li className="mb-2xs">Wavebid: +1 833-616-7370</li>
                </ul>
            </BodyPrimary>
            <BodyPrimary className="mb-sm">Germany: +49 (0)40 303731800</BodyPrimary>
            <BodyPrimary className="mb-sm">
                UK:
                <ul className="mb-sm ml-md list-disc">
                    <li className="mb-2xs">ATG: +44 (0) 203 725 5500</li>
                    <li className="mb-2xs">Antiques Trade Gazette: +44 (0) 20 3725 5507</li>
                </ul>
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                <strong>Via post</strong>
                <ul className="mb-sm ml-md list-disc">
                    <li className="mb-2xs">
                        USA - Auction Technology Group 10 E 38th St Floor 4 New York NY 10016 USA
                    </li>
                    <li className="mb-2xs">
                        Germany (EU Representative Office) - Auction Technology Group Germany GmbH Große Bäckerstraße 9
                        20095 Hamburg Germany
                    </li>
                    <li className="mb-2xs">UK - Auction Technology Group 65 Southwark Street London SE1 0HR UK</li>
                </ul>
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                To escalate a matter to our independent Group Data Protection Office contact the DPO via:
                <ul className="mb-sm ml-md list-disc">
                    <li className="mb-2xs">the contact us or support pages on our websites or</li>
                    <li className="mb-2xs">email at DPO@AuctionTechnologyGroup.com</li>
                </ul>
            </BodyPrimary>
            <H2 className="mb-xs mt-md">How do we use personal data?</H2>
            <BodyPrimary className="mb-sm">
                Our use of personal data depends on which services you are using as outlined below:
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">If you visit our websites</H3>
            <BodyPrimary className="mb-sm">
                If you visit our websites we will process details of your IT device (such as your computer, phone,
                tablet, or other device) and website usage to help manage website security and website performance as a
                reasonable and legitimate business interest.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Our websites contain links to and from other external websites such as those of auctioneers, our partner
                networks, advertisers, and affiliates. If you follow a link to any external websites, please note that
                these websites have their own processes and privacy notices and that we do not accept any responsibility
                or liability for these notices, for data you give to those organisations, nor for their processing.
                Please check their notices before you share any personal data with those websites or organisations.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Our websites also use cookies to store data on your IT device related to your use of our websites.
                Further details can be found on our Cookie Notice page which can be accessed via the link at the bottom
                of our webpages. If you have a Facebook account we will share your ATG account flag so that Facebook can
                send you targeted marketing but only if you have opted in to this via your Facebook account. Please
                contact us if you want to opt out using the details above.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">If you contact us</H3>
            <BodyPrimary className="mb-sm">
                We will use the information you provide including your user details and contact details to discuss the
                matter you have raised as a reasonable and legitimate business interest.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">If you subscribe to our communications</H3>
            <BodyPrimary className="mb-sm">
                If you subscribe to our newsletters, promotional communications, and updates, we will process your user
                and contact details to provide the requested information as a reasonable and legitimate business
                interest. You can unsubscribe from those communications at any time by:
                <ul className="mb-sm ml-md list-disc">
                    <li className="mb-2xs">Contacting us via contact details provided on this website;</li>
                    <li className="mb-2xs">Using the "unsubscribe" link in emails or "STOP" number in texts; or</li>
                    <li className="mb-2xs">
                        Updating your marketing preferences via the preference centre for your account.
                    </li>
                </ul>
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">
                If you participate in a survey, competition, loyalty reward program, webcast, or promotion
            </H3>
            <BodyPrimary className="mb-sm">
                We will process the data you provide for that specific activity including user and contact details. This
                may include postal, telephone, email, SMS, or social media communications related to that activity.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Some activities will also provide the option to opt-in to marketing updates and offers if you provide
                your consent.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">If you join our auction community by registering on our site</H3>
            <BodyPrimary className="mb-sm">
                If you register on our website, we will process the personal data that is necessary for us to provide
                our services to you including sign-up identification, service updates, product updates, bidding,
                winning, payment, any payment defaults, shipping, feedback, legal compliance, and bidder support under
                our contractual obligations.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Over time, as necessary for the fulfilment of the contract, personal data will include user contact
                details, IT device, website usage, bid, purchase, risk, and payment histories.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We may send newsletters, a timely reminder of lots, watchlists, or auctions for which you have
                registered via SMS, in-app notifications, or email as a reasonable and legitimate business interest so
                you are less likely to miss an auction. You can opt out from receiving these messages any time.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We may perform risk-based analysis of bidder data for fraud and crime prevention purposes as a public
                interest and to determine your eligibility to bid during sign-up and once you have become a registered
                bidder. We may notify our business partners such as auctioneers of potential high-risk activities to
                help protect our community by detecting, investigating, and preventing unusual account activities.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We contact bidders directly via email with communications that are necessary for contractual or legal
                reasons such as operational issues, delivery information, or payment updates.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Adverts that have been specifically selected for you will also be presented on other websites such as
                social media websites that you visit as a legitimate business interest. Those websites use their cookies
                (with your consent) or one-way-encrypted (hashed) email address to identify your account. Those websites
                will not have access to your auctioneer or bidder account or bidding data. You can opt out of this by
                changing your account setting or preferences on these websites.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We also anonymise user data so that we can anonymously analyse aggregated customer trends and business
                performance over time as a legitimate business interest.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Bidding</H3>
            <BodyPrimary className="mb-sm">
                If you register to bid in an auction via one of our websites we will share your contact details and bid,
                purchase, and payment histories via our website with the relevant auctioneer to enable the auctioneer to
                determine whether to approve you to bid in their auction.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Winning Bids</H3>
            <BodyPrimary className="mb-sm">
                If you win a lot as a result of a bid placed via our website we will share your contact details,
                purchase, and payment details (using a tokenised version of your payment card) with the auctioneer for
                that auction for them to fulfil the order. That auctioneer is responsible for confirming your winning
                bid and delivering the goods you have purchased from them. Where we are shipping the lot we will also
                process your details as necessary for the delivery including the necessary sharing of your data with
                shipping companies.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Those auctioneers and shipping companies will be operating as independent data controllers for the
                purpose of processing that data. We have no control or liability for the use of data by those companies
                and their own policies and notices apply.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Any issues with the goods you have purchased must be raised directly with the auctioneer.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Payments</H3>
            <BodyPrimary className="mb-sm">
                Where financial transactions require bank account details to be processed we will only use those details
                for the purpose of agreed payments.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Payment cards services for bidders are provided by specialist third parties such as Payrix, Plug'n Pay
                Technologies, and Global Payments who operate as independent data controllers. Their policies, notices,
                and controls apply. We will retain partial information such as a "card transaction token".
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We store your Bank Account Information (including the name, routing number, account number, and current
                balance of your account at a financial institution from which you may make payments). We use Plaid to
                authenticate information regarding your account. We may use this information to execute transactions and
                debit your account.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Protecting Our Auction Community</H3>
            <BodyPrimary className="mb-sm">
                We take reasonable proactive steps to help protect all parties that we work with from illegal, unfair,
                or inappropriate behaviours as a reasonable public interest.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We will monitor positive and negative account activities including user contact details, IT device,
                website usage, bid, purchase, and payment histories. We share such information with partners such as
                auctioneers and relevant third-party service providers to help all parties manage their commercial risks
                and prevent fraud under our collaborative auction service.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Accounts and services may be suspended, blocked, or closed where behaviours are thought by us in our
                sole discretion to be inappropriate or create a commercial risk. You will be notified directly of any
                such action if applicable to you. To dispute an account action contact our customer services team.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We reserve the right to take legal or debt collection actions against organisations or individuals where
                relevant and will share data with necessary parties such as law enforcement, debt collection agencies,
                and credit bureaus in order to do so.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">White Labelled Auction Platforms</H3>
            <BodyPrimary className="mb-sm">
                Where we are providing technological services to an auctioneer such that you are the direct customer of
                the auctioneer, the auctioneer is the data controller and we are their data processor for this
                processing. The auctioneer is responsible for the use of your data. Any requests you wish to raise must
                be raised directly with the auctioneer for that auction.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Engaging as an Auctioneer or Seller</H3>
            <BodyPrimary className="mb-sm">
                We will process contact details of the people working for auctioneer in order to professionally engage
                with your organisations for business-to-business purposes as a legitimate business interest.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Journalism</H3>
            <BodyPrimary className="mb-sm">
                We will publish information that we believe to be newsworthy in relation to our business or auction
                industry as a public interest. This can include names, locations, companies, and auction-related
                information.
            </BodyPrimary>
            <H2 className="mb-xs mt-md">Your rights</H2>
            <BodyPrimary className="mb-sm">
                We respect your right to not be discriminated against by us because you exercised any of your rights
                under applicable data protection legislation. This means we will not among other things on the basis of
                your data subject request:
            </BodyPrimary>
            <ul className="mb-sm ml-md list-disc">
                <li className="mb-2xs">Deny goods or services to you;</li>
                <li className="mb-2xs">
                    Charge different prices or rates for goods or services including through the use of discounts or
                    other benefits or imposing penalties;
                </li>
                <li className="mb-2xs">Provide a different level or quality of goods or services to you; or</li>
                <li className="mb-2xs">
                    Suggest that you will receive a different price or rate for goods or services or a different level
                    or quality of goods or services.
                </li>
            </ul>
            <H3 className="mb-2xs mt-sm">
                For residents of the UK and EU - Your Legal Rights Under the General Data Protection Regulation
            </H3>
            <BodyPrimary className="mb-sm">
                If we are processing your personal data you have the right to ask that we:
            </BodyPrimary>
            <ul className="mb-sm ml-md list-disc">
                <li className="mb-2xs">
                    Tell you how your data is being used including how data is processed by third parties on our behalf;
                </li>
                <li className="mb-2xs">Send you or your nominated third party a copy of your data;</li>
                <li className="mb-2xs">
                    Stop or suspend processing of your data or opt out of consent that you have provided including our
                    use of data for marketing;
                </li>
                <li className="mb-2xs">Correct or erase your data; and/or</li>
                <li className="mb-2xs">
                    Explain and manually review any automated decision making or profiling of your data.
                </li>
            </ul>
            <H3 className="mb-2xs mt-sm">
                For Residents of Certain US states - Your Legal Rights Under the California Consumer Privacy Act of 2018
                ("CCPA") and other state privacy laws
            </H3>
            <BodyPrimary className="mb-sm">
                If you are a California resident you have certain rights regarding your personal data (referred to as
                "personal information") under the CCPA including the right to request that we delete or correct personal
                information that we have collected from you; the right to opt-out of the sale or sharing of your
                personal information with our service providers; the right to limit our use of your sensitive personal
                information; and the right not to be discriminated against for exercising your rights. These rights are
                subject to certain exceptions.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                Under California laws you or your authorized agent have the right to know what ATG collects or discloses
                about you in the last 12 months related to your account:
            </BodyPrimary>
            <ul className="mb-sm ml-md list-disc">
                <li className="mb-2xs">The categories of personal information we have collected.</li>
                <li className="mb-2xs">The categories of sources from which the personal information is collected.</li>
                <li className="mb-2xs">
                    The business or commercial purpose for collecting or selling personal information.
                </li>
                <li className="mb-2xs">The categories of third parties with whom we share personal information.</li>
                <li className="mb-2xs">The specific pieces of personal information we have collected about you.</li>
            </ul>
            <BodyPrimary className="mb-sm">
                If you would like to exercise any of the privacy rights provided to you under your applicable state law
                please refer to the "How to Contact Us" section of this policy.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Responding to your requests</H3>
            <BodyPrimary className="mb-sm">
                Before responding we will need to take steps to verify your identity for reasons of security (e.g.
                confirming your information we have on file). Once your identity is verified responses will typically be
                completed within one month and at no cost to you.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                We will endeavour to fulfil your request but may be subject to limitations in our responses for legal or
                contractual reasons. For example we will not delete data if there is a legal or contractual obligation
                for us to retain that data. We will provide specific details of these limitations as relevant to your
                request if and when applicable.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                To exercise any of the above listed rights please contact our Customer Service teams using the contact
                details provided on this website highlighting that you are raising a Data Subject Request.
            </BodyPrimary>
            <BodyPrimary className="mb-sm">
                If you are not happy with the way we have handled your data you have the right to escalate the matter to
                our Group independent Data Protection Office (
                <Link href="mailto:DPO@AuctionTechnologyGroup.com">DPO@AuctionTechnologyGroup.com</Link>) to our
                supervisory authority (the Information Commissioner's Office at ico.org.uk) or to initiate legal action.
                However if you ever have any problems we ask that you contact our Customer Service teams first so that
                we can help to resolve the issue directly using the contact details provided above.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Data Retention</H3>
            <BodyPrimary className="mb-sm">
                We retain data for as long as we believe is required to fulfil the purpose that the data was collected
                for or to address potential associated legal or operational matters.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Categories of Personal Data</H3>
            <BodyPrimary className="mb-sm">
                Where categories of personal data are referenced the following types of personal data are included.
            </BodyPrimary>
            <table className="mb-md w-full border-collapse">
                <thead>
                    <tr className="border-b border-surface-200">
                        <th className="p-xs text-left font-bold">Categories of Personal Data</th>
                        <th className="p-xs text-left font-bold">Specific Types of Personal Data Collected</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">User details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Name (First, Last)</li>
                                <li className="mb-2xs">Username and Password</li>
                                <li className="mb-2xs">
                                    Passport Number and Issuing Country (Verification of auctioneer business owners)
                                </li>
                                <li className="mb-2xs">Communication preferences (e.g. marketing)</li>
                                <li className="mb-2xs">Complaints, issues, queries, comments, feedback</li>
                                <li className="mb-2xs">Event participation flag (e.g. surveys or competitions)</li>
                                <li className="mb-2xs">
                                    Risk indicators (e.g. feedback, blocks or bans initiated by ourselves or
                                    auctioneers, payment issues, defaults, bidding issues, device issues, location
                                    issues, etc)
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">Contact details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Postal address</li>
                                <li className="mb-2xs">Phone number</li>
                                <li className="mb-2xs">Cell phone number</li>
                                <li className="mb-2xs">Email address</li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">IT Device details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">IP Address</li>
                                <li className="mb-2xs">Login credentials</li>
                                <li className="mb-2xs">Type of operating system and browser</li>
                                <li className="mb-2xs">Type of device and operating system</li>
                                <li className="mb-2xs">Geographical location</li>
                                <li className="mb-2xs">Preferred language</li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">Website usage details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Pages viewed</li>
                                <li className="mb-2xs">Links clicked (webpages and emails)</li>
                                <li className="mb-2xs">Date and time of visit</li>
                                <li className="mb-2xs">Webpage performance errors and debugging data</li>
                                <li className="mb-2xs">Duration of visit</li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">Bid details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Search criteria</li>
                                <li className="mb-2xs">Items viewed</li>
                                <li className="mb-2xs">Auctions Registered</li>
                                <li className="mb-2xs">Items bid on, amount, date, and time.</li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">Purchase details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Product</li>
                                <li className="mb-2xs">Date and time of winning bid</li>
                                <li className="mb-2xs">Amount of winning bid</li>
                                <li className="mb-2xs">Delivery address</li>
                                <li className="mb-2xs">Auctioneer comments and feedback</li>
                            </ul>
                        </td>
                    </tr>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">Payment details</td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Bank account information (routing number and account number)</li>
                                <li className="mb-2xs">Payment card information stored as a card transaction token</li>
                                <li className="mb-2xs">Timing and status of payments</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
            <BodyPrimary className="mb-sm">
                We do not collect the following GDPR Special Categories of personal data about you regarding race or
                ethnicity, political opinions, religious or philosophical beliefs, trade union membership, genetics,
                biometrics, health, sex life, or sexual orientation. We also do not collect data about criminal
                convictions or offenses. We request that you refrain from providing such data. We may collect certain
                data about you that may be considered sensitive personal data in some jurisdictions for purposes
                described above.
            </BodyPrimary>
            <H3 className="mb-2xs mt-sm">Other Matters</H3>
            <BodyPrimary className="mb-sm">
                We routinely transfer personal information to external processing organisations (Processors) who help us
                provide our services under written contractual agreement such that they are only authorised to use the
                information for our purposes. External organisations include:
            </BodyPrimary>
            <table className="mb-md w-full border-collapse">
                <thead>
                    <tr className="border-b border-surface-200">
                        <th className="p-xs text-left font-bold">Third party service providers</th>
                        <th className="p-xs text-left font-bold">What information we share</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b border-surface-200">
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Analytics providers</li>
                                <li className="mb-2xs">ATG companies and auditors</li>
                                <li className="mb-2xs">
                                    Business service providers including insurance, IT, finance, marketing, operational,
                                    and security support
                                </li>
                                <li className="mb-2xs">Credit reference agencies</li>
                                <li className="mb-2xs">Law enforcement agencies</li>
                                <li className="mb-2xs">Marketing partners and social media sites</li>
                                <li className="mb-2xs">Professional service providers</li>
                                <li className="mb-2xs">Research companies</li>
                            </ul>
                        </td>
                        <td className="p-xs">
                            <ul className="mb-sm ml-md list-disc">
                                <li className="mb-2xs">Analytics information</li>
                                <li className="mb-2xs">Auctioneer feedback</li>
                                <li className="mb-2xs">Bidder account information</li>
                                <li className="mb-2xs">Customer support information</li>
                                <li className="mb-2xs">Survey responses</li>
                                <li className="mb-2xs">Transaction details</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
            <BodyPrimary className="mb-sm">
                We do not sell personal information or aggregated personal information. We do not permit use of our
                services by children under the age of 16. We do aggregate personal data for the purpose of internal
                management reporting.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                If you do not provide personal information we ask for it may delay or prevent us from providing
                associated services to you.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We only collect personal data directly from the data subject via our electronic platforms. We do not
                purchase personal data from third parties.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We will share personal data with governmental or law enforcement authorities where required to do so to
                comply with legal obligations or to prevent, detect, investigate, or prosecute crimes, losses, or
                damages.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We may also need to share your data with other parties such as potential buyers of some or all of our
                business or during a re-structuring. We will typically anonymize information but this may not always be
                possible. The recipient of the information will be bound by confidentiality obligations. If your data is
                transferred to a third-party organisation as a result of corporate merger or acquisition activities this
                privacy notice will still apply unless a privacy notice is provided to you by the third party.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We engage third party processors, including the other ATG entities under contractual agreement to
                process data on our behalf.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                Other than stated in this Privacy Notice we will not share, trade, sell, or rent data to other third
                parties. We do not allow our third-party service providers to use your personal data for their own
                purposes.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                Where data is transferred internationally, appropriate safeguards will be applied such as transfers to
                countries approved as having adequate data protection legislation, necessity for contract, approved
                contractual clauses, international data transfer agreements, or US data bridge registrations.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We use extensive operational, technical, physical, and organisational measures to help protect personal
                data from unauthorised access, loss, unlawful processing, damage, or destruction.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                You should only share payments data via secure portals which are directly processed by our payment
                providers as independent data controllers.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                This privacy notice has been written with brevity and usability in mind to help you easily understand
                how we use personal data. If you have any further questions or comments or would like more detail,
                please contact our Customer Services team directly.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">
                We reserve the right to update this information at any time. If we make material changes to this privacy
                notice we will take reasonable steps to notify affected data subjects.
            </BodyPrimary>
            <br />
            <br />
            <BodyPrimary className="mb-sm">This privacy notice was last updated July 2024.</BodyPrimary>
        </div>
    );
};

export default PrivacyNotice;
